import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Upload, Loader2, X, Car, FileText } from 'lucide-react';
import { compressImage } from '../utils/compressImage';

import { getApiBaseUrl } from '../utils/apiUrl';
const API_URL = getApiBaseUrl();

const PHOTO_LABELS = {
    front: 'Frente',
    back: 'Traseira', 
    left: 'Lateral Esquerda',
    right: 'Lateral Direita',
};

export default function VehicleStep({ formData, setFormData, onNext, onBack }) {
    const [uploading, setUploading] = useState({});
    const v = formData.vehicle_data || {};
    const photos = v.vehicle_photos || { front: '', back: '', left: '', right: '' };

    const updateVehicle = (field, value) => {
        setFormData(prev => ({
            ...prev,
            vehicle_data: { ...prev.vehicle_data, [field]: value }
        }));
    };

    const updatePhoto = (position, url) => {
        setFormData(prev => ({
            ...prev,
            vehicle_data: {
                ...prev.vehicle_data,
                vehicle_photos: { ...prev.vehicle_data.vehicle_photos, [position]: url }
            }
        }));
    };

    const handleUpload = async (rawFile, position) => {
        setUploading(prev => ({ ...prev, [position]: true }));
        try {
            const file = await compressImage(rawFile, { maxWidth: 1200, maxHeight: 1200, quality: 0.7 });
            const fd = new FormData();
            fd.append('file', file);
            const token = localStorage.getItem('receppet_token');
            const headers = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 45000);
            const res = await fetch(`${API_URL}/api/upload/image/public`, {
                method: 'POST', body: fd, headers, signal: controller.signal,
            });
            clearTimeout(timeoutId);
            if (!res.ok) throw new Error(`Erro ${res.status}`);
            const data = await res.json();
            updatePhoto(position, data.url);
            toast.success(`Foto ${PHOTO_LABELS[position]} enviada`);
        } catch (e) {
            if (e.name === 'AbortError') {
                toast.error('Upload demorou demais. Tente com uma foto menor.');
            } else {
                toast.error(`Erro ao enviar foto: ${e.message}`);
            }
        } finally {
            setUploading(prev => ({ ...prev, [position]: false }));
        }
    };

    const handleCnhUpload = async (rawFile) => {
        setUploading(prev => ({ ...prev, cnh: true }));
        try {
            const file = await compressImage(rawFile, { maxWidth: 1400, maxHeight: 1400, quality: 0.75 });
            const fd = new FormData();
            fd.append('file', file);
            const token = localStorage.getItem('receppet_token');
            const headers = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 45000);
            const res = await fetch(`${API_URL}/api/upload/image/public`, {
                method: 'POST', body: fd, headers, signal: controller.signal,
            });
            clearTimeout(timeoutId);
            if (!res.ok) throw new Error(`Erro ${res.status}`);
            const data = await res.json();
            updateVehicle('cnh_photo_url', data.url);
            toast.success('Foto da CNH enviada');
        } catch (e) {
            if (e.name === 'AbortError') {
                toast.error('Upload da CNH demorou demais. Tente com uma foto menor.');
            } else {
                toast.error(`Erro ao enviar CNH: ${e.message}`);
            }
        } finally {
            setUploading(prev => ({ ...prev, cnh: false }));
        }
    };

    const handleDocUpload = async (rawFile, field, label) => {
        setUploading(prev => ({ ...prev, [field]: true }));
        try {
            const file = await compressImage(rawFile, { maxWidth: 1400, maxHeight: 1400, quality: 0.75 });
            const fd = new FormData();
            fd.append('file', file);
            const token = localStorage.getItem('receppet_token');
            const headers = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 45000);
            const res = await fetch(`${API_URL}/api/upload/image/public`, {
                method: 'POST', body: fd, headers, signal: controller.signal,
            });
            clearTimeout(timeoutId);
            if (!res.ok) throw new Error(`Erro ${res.status}`);
            const data = await res.json();
            updateVehicle(field, data.url);
            toast.success(`${label} enviado`);
        } catch (e) {
            if (e.name === 'AbortError') {
                toast.error(`Upload do ${label} demorou demais. Tente com uma foto menor.`);
            } else {
                toast.error(`Erro ao enviar ${label}: ${e.message}`);
            }
        } finally {
            setUploading(prev => ({ ...prev, [field]: false }));
        }
    };

    return (
        <div className="space-y-5" data-testid="vehicle-step">
            {/* Vehicle Info */}
            <div className="space-y-3">
                <h3 className="font-semibold text-sm text-slate-700 border-b pb-2 flex items-center gap-2">
                    <Car className="w-4 h-4" /> Dados do Veículo
                </h3>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                        <Label>Tipo *</Label>
                        <Select value={v.vehicle_type} onValueChange={val => updateVehicle('vehicle_type', val)}>
                            <SelectTrigger data-testid="vehicle-type"><SelectValue placeholder="Selecione" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="car">Carro</SelectItem>
                                <SelectItem value="van">Van</SelectItem>
                                <SelectItem value="suv">SUV</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1">
                        <Label>Cor *</Label>
                        <Input value={v.vehicle_color} onChange={e => updateVehicle('vehicle_color', e.target.value)} placeholder="Ex: Branco" data-testid="vehicle-color" />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                        <Label>Marca *</Label>
                        <Input value={v.vehicle_brand} onChange={e => updateVehicle('vehicle_brand', e.target.value)} placeholder="Ex: Fiat" data-testid="vehicle-brand" />
                    </div>
                    <div className="space-y-1">
                        <Label>Modelo *</Label>
                        <Input value={v.vehicle_model} onChange={e => updateVehicle('vehicle_model', e.target.value)} placeholder="Ex: Argo" data-testid="vehicle-model" />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                        <Label>Ano</Label>
                        <Input value={v.vehicle_year} onChange={e => updateVehicle('vehicle_year', e.target.value)} placeholder="2024" data-testid="vehicle-year" />
                    </div>
                    <div className="space-y-1">
                        <Label>Placa *</Label>
                        <Input value={v.vehicle_plate} onChange={e => updateVehicle('vehicle_plate', e.target.value.toUpperCase())} placeholder="ABC1D23" data-testid="vehicle-plate" />
                    </div>
                </div>
            </div>

            {/* 4 Vehicle Photos */}
            <div className="space-y-3">
                <h3 className="font-semibold text-sm text-slate-700 border-b pb-2">
                    Fotos do Veículo * <span className="font-normal text-slate-400">(4 obrigatórias)</span>
                </h3>
                <div className="grid grid-cols-2 gap-3">
                    {Object.entries(PHOTO_LABELS).map(([key, label]) => (
                        <div key={key} className="space-y-1">
                            <Label className="text-xs">{label}</Label>
                            {photos[key] ? (
                                <div className="relative w-full h-24 rounded-lg overflow-hidden bg-slate-100">
                                    <img src={photos[key]} alt={label} className="w-full h-full object-cover" />
                                    <button type="button" onClick={() => updatePhoto(key, '')} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5">
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ) : (
                                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#4A90B2] transition-colors" data-testid={`vehicle-photo-${key}`}>
                                    {uploading[key] ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
                                        <><Upload className="w-4 h-4 text-slate-400" /><span className="text-xs text-slate-500 mt-1">{label}</span></>
                                    )}
                                    <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0], key)} />
                                </label>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* CNH */}
            <div className="space-y-3">
                <h3 className="font-semibold text-sm text-slate-700 border-b pb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> CNH *
                </h3>
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                        <Label>Numero *</Label>
                        <Input value={v.cnh_number} onChange={e => updateVehicle('cnh_number', e.target.value)} placeholder="00000000000" data-testid="cnh-number" />
                    </div>
                    <div className="space-y-1">
                        <Label>Categoria *</Label>
                        <Select value={v.cnh_category} onValueChange={val => updateVehicle('cnh_category', val)}>
                            <SelectTrigger data-testid="cnh-category"><SelectValue placeholder="Cat." /></SelectTrigger>
                            <SelectContent>
                                {['A', 'B', 'AB', 'C', 'D', 'E'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="space-y-1">
                    <Label>Validade *</Label>
                    <Input type="date" value={v.cnh_expiry} onChange={e => updateVehicle('cnh_expiry', e.target.value)} data-testid="cnh-expiry" />
                </div>
                <div className="space-y-1">
                    <Label>Foto da CNH *</Label>
                    {v.cnh_photo_url ? (
                        <div className="relative w-full h-32 rounded-lg overflow-hidden bg-slate-100">
                            <img src={v.cnh_photo_url} alt="CNH" className="w-full h-full object-cover" />
                            <button type="button" onClick={() => updateVehicle('cnh_photo_url', '')} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5">
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ) : (
                        <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#4A90B2] transition-colors" data-testid="cnh-photo-input">
                            {uploading.cnh ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
                                <><Upload className="w-4 h-4 text-slate-400 mr-2" /><span className="text-sm text-slate-500">Enviar foto da CNH</span></>
                            )}
                            <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleCnhUpload(e.target.files[0])} />
                        </label>
                    )}
                </div>
            </div>

            {/* CRLV */}
            <div className="space-y-3">
                <h3 className="font-semibold text-sm text-slate-700 border-b pb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> CRLV (Documento do Veiculo) *
                </h3>
                <div className="space-y-1">
                    <Label>Foto do CRLV *</Label>
                    {v.crlv_url ? (
                        <div className="relative w-full h-32 rounded-lg overflow-hidden bg-slate-100">
                            <img src={v.crlv_url} alt="CRLV" className="w-full h-full object-cover" />
                            <button type="button" onClick={() => updateVehicle('crlv_url', '')} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5">
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ) : (
                        <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#4A90B2] transition-colors" data-testid="crlv-photo-input">
                            {uploading.crlv_url ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
                                <><Upload className="w-4 h-4 text-slate-400 mr-2" /><span className="text-sm text-slate-500">Enviar foto do CRLV</span></>
                            )}
                            <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleDocUpload(e.target.files[0], 'crlv_url', 'CRLV')} />
                        </label>
                    )}
                    <p className="text-xs text-slate-400">Certificado de Registro e Licenciamento de Veiculo</p>
                </div>
            </div>

            {/* Equipment */}
            <div className="space-y-3">
                <h3 className="font-semibold text-sm text-slate-700 border-b pb-2">Equipamentos</h3>
                {[['has_air_conditioning', 'Ar-condicionado'], ['has_pet_seat', 'Assento/cinto para pet'], ['has_pet_crate', 'Caixa de transporte']].map(([key, label]) => (
                    <div key={key} className="flex items-center gap-2">
                        <Checkbox id={`v-${key}`} checked={v[key]} onCheckedChange={c => updateVehicle(key, c)} />
                        <Label htmlFor={`v-${key}`} className="cursor-pointer text-sm">{label}</Label>
                    </div>
                ))}
                <div className="space-y-1">
                    <Label>Peso máximo do pet (kg)</Label>
                    <Input type="number" value={v.max_pet_weight} onChange={e => updateVehicle('max_pet_weight', e.target.value)} placeholder="30" data-testid="vehicle-max-weight" />
                </div>
            </div>

            {/* Navigation */}
            <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={onBack} className="flex-1" data-testid="vehicle-back-btn">
                    <ChevronLeft className="w-4 h-4 mr-2" />Voltar
                </Button>
                <Button type="button" onClick={onNext} className="flex-1 btn-primary" data-testid="vehicle-next-btn">
                    Continuar<ChevronRight className="w-4 h-4 ml-2" />
                </Button>
            </div>
        </div>
    );
}
